import type { StepExtended } from '@epilot/journey-logic-commons'
import type { CoreActions, JsonFormsCore } from '@jsonforms/core'
import { JsonForms } from '@jsonforms/react'
import { once } from 'radashi'
import { memo, useEffect, type ComponentProps } from 'react'

import {
  useAdditionalErrorsFromState,
  useJourneyContext
} from '../../../blocks-renderers'
import { TIME_TO_FIRST_RENDER_METRIC } from '../../../utils/config'
import { initAJV } from '../../../utils/initAJV'
import { getRenderers } from '../../../utils/renderers'
import { endTimer } from '../../../utils/timers'

/**
 * Ensures the underlying metric is stopped only once.
 */
const endTimeToFirstRender = once(
  () => void endTimer(TIME_TO_FIRST_RENDER_METRIC)
)

const errorMiddlewareFactory = (debug: boolean) => {
  if (!debug) return undefined

  return (
    state: JsonFormsCore,
    action: CoreActions,
    defaultReducer: (state: JsonFormsCore, action: CoreActions) => JsonFormsCore
  ) => {
    const { data, errors } = state

    if (errors && errors.length) {
      // eslint-disable-next-line no-console
      console.debug('Detected JSON Forms errors', { errors, data })
    }

    return defaultReducer(state, action)
  }
}

export type StepContentProps = Omit<
  ComponentProps<typeof JsonForms>,
  'additionalErrors' | 'ajv' | 'middleware' | 'renderers' | 'validationMode'
> & {
  /** Which Step to render the content */
  step: StepExtended
  /** Whether to enable debugging capabilities */
  debug?: boolean
}

const StepContent = memo(({ debug, step, ...formProps }: StepContentProps) => {
  const ajv = initAJV()
  // TODO: Expensive operation, move to AppStore
  const validationMode = useJourneyContext().context.errorValidationMode
  const additionalErrors = useAdditionalErrorsFromState(
    formProps?.data,
    validationMode,
    step
  )

  /**
   * Marks the end of the first render.
   * NOTE: This should only be called once, after the component is mounted the first time.
   */
  useEffect(() => void endTimeToFirstRender(), [])

  return (
    <JsonForms
      additionalErrors={additionalErrors}
      ajv={ajv}
      middleware={errorMiddlewareFactory(Boolean(debug))}
      renderers={getRenderers()}
      validationMode={validationMode}
      {...formProps}
    />
  )
})

StepContent.displayName = 'StepContent'

export default StepContent // for lazy loading
